import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import RegisterLocation from '@/components/features/LocationDetail/RegisterLocation';
import { MemoizedMap as Map } from '@/components/features/LocationDetail/shared/Map';
import { FilterUnitOverview } from '@/components/features/RegistrationCompositeDetail/FilterUnitOverview';
import { AccountView, ActionModal, AlertTypes, Button, Icon, Link, Welcome } from '@/components/shared';
import { MoveModal } from '@/components/shared/ApplicationsList/MoveModal';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { getListApplicationRegistrationsGroupedQueryKey } from '@/generated/application-registrations';
import { useGetSelectedCustomer } from '@/generated/customer';
import {
	AccountId,
	RegistrationCompositeDetailsResponse,
	Area,
	LocationType,
	PageType,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useDeleteRegistrationComposite } from '@/generated/registration-composite';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { decimalToCoordinates } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './ApplicationDetail.module.scss';
import { ApplicationOverview } from './ApplicationOverview';

interface ApplicationDetailProps {
	accountId: AccountId;
	registrationCompositeId: RegistrationCompositeId;
	application?: RegistrationCompositeDetailsResponse;
}

const RegistrationCompositeDetail: React.FunctionComponent<ApplicationDetailProps> = ({
	accountId,
	registrationCompositeId,
	application,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);
	const addNotification = useNotificationStore((state) => state.addNotification);
	const [viewDeleteModal, setViewDeleteModal] = useState(false);
	const [viewMoveModal, setViewMoveModal] = useState(false);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const location = application?.location;

	const customerPath = useMemo(() => {
		if (!layout) {
			return '/';
		}
		const path = layout?.data.staticLinks.find((route) => route.pageType === PageType.Customer)?.url;
		return path ? `${path}?accountid=${accountId}` : '/';
	}, [layout, accountId]);

	// App resolves the page from location.state, so a client navigation has to carry
	// the same pair the customer page is reached with elsewhere.
	const goToCustomer = () => {
		navigate(customerPath, { state: { pageType: PageType.Customer, area: Area.StaticPages } });
	};

	const deleteCompositeMutation = useDeleteRegistrationComposite({
		mutation: {
			onSuccess: async () => {
				// refresh application registration grouped list query cache
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});
				addNotification({
					children: translations?.data.composite.deleteComposite.success,
					type: AlertTypes.SUCCESS,
				});
				goToCustomer();
			},
			onError: () => {
				addNotification({
					children: translations?.data.composite.deleteComposite.failed,
					type: AlertTypes.DANGER,
				});
			},
		},
	});
	const { data: selectedCustomer } = useGetSelectedCustomer(segmentationId);

	const backButtonRoute = useMemo(() => {
		const path = layout?.data.staticLinks.find((route) => route.pageType === PageType.Customer)?.url;
		return {
			pageType: PageType.Customer,
			area: Area.StaticPages,
			externalRoute: path ? `${path}?accountid=${accountId}` : '/',
		};
	}, [layout, accountId]);

	const editRoute = useMemo(() => {
		const path = layout?.data.staticLinks.find((route) => route.pageType === PageType.EditLocation)?.url;
		return {
			pageType: PageType.EditLocation,
			area: Area.StaticPages,
			externalRoute: path
				? `${path}?accountid=${accountId}&registrationcompositeid=${registrationCompositeId}&locationid=${application?.location?.id}&applicationid=${application?.id}`
				: '/',
		};
	}, [layout, accountId, registrationCompositeId, application?.id, application?.location?.id]);

	const askToDeleteComposite = () => {
		setViewDeleteModal(true);
	};

	const deleteComposite = () => {
		deleteCompositeMutation.mutate({
			segmentationId: Number(segmentationId),
			accountId: Number(accountId),
			registrationCompositeId,
		});
	};

	const coordinates = React.useMemo(() => {
		if (location?.latitude && location?.longitude) {
			const lat = Number(location.latitude);
			const lng = Number(location.longitude);

			const convertedLat = decimalToCoordinates(lat);
			const convertedLng = decimalToCoordinates(lng);

			return `${convertedLat}${lat > 0 ? 'N' : 'S'} ${convertedLng}${lng > 0 ? 'E' : 'W'}`;
		}
		return null;
	}, [location?.latitude, location?.longitude]);

	return (
		<article className={'u-container-lg u-my-md'}>
			<Welcome
				backButtonRoute={backButtonRoute}
				backButtonText={translations?.data.customer.pageMetaData.title}
				backButtonAriaLabel={`${translations?.data.shared.backTo} ${translations?.data.customer.pageMetaData.title}`}
			/>

			<div className={styles.main}>
				<div className={classNames(styles.topActions, { [styles.hasLocation]: !!location })}>
					<Button onClick={() => setViewMoveModal(true)} variant={'outline'} className={styles.actionButton}>
						{translations?.data.composite.moveComposite.button}
						<Icon name={'swap'} size={'sm'} />
					</Button>

					<Button onClick={askToDeleteComposite} variant={'outline'} className={styles.actionButton}>
						{translations?.data.composite?.deleteComposite.button}
						<Icon name={'trash'} size={'sm'} />
					</Button>
				</div>
				{location ? (
					<div className={styles.location}>
						<div className={styles.locationContent}>
							<p className={styles.locationName}>{translations?.data.locations.locationDetails}</p>

							{location.locationType === LocationType.Site && (
								<>
									<p className={styles.address}>{location.address}</p>
									<p className={styles.address}>{coordinates}</p>
								</>
							)}

							<dl className={styles.locationList}>
								{location.locationType === LocationType.Site ? (
									<>
										<div>
											<dt>{translations?.data.locations.type}:</dt>
											<dd>{translations?.data.locations.site}</dd>
										</div>

										{application.group && (
											<div>
												<dt>{translations?.data.applications.group}:</dt>
												<dd>{application.group}</dd>
											</div>
										)}

										<div>
											<dt>{translations?.data.locations.identifier}:</dt>
											<dd>{location.identifier}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.ownerOperator}:</dt>
											<dd>{location.owner}</dd>
										</div>
									</>
								) : (
									<>
										<div>
											<dt>{translations?.data.locations.type}:</dt>
											<dd>{translations?.data.locations.vessel}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.vesselType}:</dt>
											<dd>{location.vesselType}</dd>
										</div>

										{application.group && (
											<div>
												<dt>{translations?.data.applications.group}:</dt>
												<dd>{application.group}</dd>
											</div>
										)}

										<div>
											<dt>{translations?.data.locations.iMOnumber}:</dt>
											<dd>{location.identifier}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.technicalManager}:</dt>
											<dd>{location.operator}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.charterer}:</dt>
											<dd>{location.charterer}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.shipOwner}:</dt>
											<dd>{location.owner}</dd>
										</div>

										<div>
											<dt>{translations?.data.locations.shipyard}:</dt>
											<dd>{location.shipyard}</dd>
										</div>
									</>
								)}
							</dl>

							{location.description && (
								<div dangerouslySetInnerHTML={{ __html: location.description }}></div>
							)}

							<Button
								className={styles.locationEdit}
								aria-label={translations?.data.shared.edit}
								variant={'ghost'}
								route={editRoute}
							>
								<Icon name={'edit'} size={'sm'} />
							</Button>
						</div>

						{location.locationType === LocationType.Site ? (
							<Map
								latitude={location.latitude ? Number(location.latitude) : undefined}
								longitude={location.longitude ? Number(location.longitude) : undefined}
								apiKey={settings?.data.googleMapsApiKey || ''}
							/>
						) : location.marineTrafficLink ? (
							<div className={styles.locationContent}>
								<Link
									className={styles.marineLink}
									linkUrl={location.marineTrafficLink}
									hideExternalIcon
									target="_blank"
									rel="noreferrer"
								>
									{translations?.data.locations.link}
								</Link>
							</div>
						) : null}
					</div>
				) : (
					<RegisterLocation
						accountId={accountId}
						registrationCompositeId={registrationCompositeId}
						applicationId={application?.id}
					/>
				)}
				<div className={classNames(styles.content, 'u-mt-md')}>
					<ApplicationOverview
						application={application}
						accountId={accountId}
						registrationCompositeId={registrationCompositeId}
					/>
					<FilterUnitOverview
						accountId={accountId}
						registrationCompositeId={registrationCompositeId}
						application={application}
						selectedCustomer={selectedCustomer?.data}
					/>
				</div>

				<AccountView accountId={accountId} wrapperClassName="u-mt-md" />
			</div>
			{viewMoveModal && (
				<MoveModal
					accountId={accountId}
					registrationCompositeId={registrationCompositeId}
					filterUnitId={application?.filterUnitId}
					hasLocation={!!location}
					onClose={() => setViewMoveModal(false)}
					// Unlike the account overview, the composite being moved is this
					// page. An account move takes it off this account, and a filter unit
					// move deletes the source when it holds no location and no
					// application, so there is nothing left to show either way.
					onMoved={goToCustomer}
				/>
			)}
			{viewDeleteModal && (
				<ActionModal
					size={'sm'}
					translations={translations?.data}
					onSubmit={deleteComposite}
					onCancel={() => setViewDeleteModal(false)}
					headline={translations?.data.composite.deleteComposite.headline}
					message={translations?.data.composite.deleteComposite.teaser}
				/>
			)}
		</article>
	);
};

export default RegistrationCompositeDetail;
